import React, { useMemo, useState, FC, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import container, {
  translateMain,
  translateLogin,
  translateWeb,
  normalizeOTP,
  normalizeUsername,
  normalizeMobileNumber,
  normalizePassword,
} from './container'
import FormField from '../../shared/newFormElements'
import { LKIcon, LKButton } from 'loankeeper-common/components'
import { loadFont } from 'loankeeper-common/helpers/styles'
import { AppDispatch } from 'src/store/index.d'
import { verifyPasswordChangeOTP, resetPassword } from 'src/store/auth'
import { validatePassword } from './validatePassword'
import PasswordValidationResult from './PasswordValitaionUI'
// @ts-ignore
import LKModal, { actions as LKModalActions } from 'shared/LKModal'
import { isOpen } from 'src/features/shared/LKModal/selectors'

import { ValidatePasswordRules } from '@lk/api'

const StyledLKIcon = styled(LKIcon)`
  &.lk-icon {
    height: 28px !important;
    width: 20px;
    position: absolute;
    left: 10px;
    top: 2px;
  }
`

type ButtonProps = {
  theme: {
    buttonFont: string
  }
}

const StyledLoginButton = styled(LKButton)`
  .ui.button {
    ${(props: ButtonProps) => loadFont(props.theme.buttonFont || '13px BPG Algeti')};
  }
`

const InputRightIcon = styled.div`
  position: absolute;
  right: -20px;
  top: 25px;
  width: 15px;
  height: 15px;
`

const StyledResetInput = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  width: 70%;
  flex-wrap: nowrap;
  margin: 0 auto;
`

type Props = {
  username: string
  mobile: string
  otpCode: string
  password: string
  repeatPassword: string
  resetinput: (inputName: string) => void
  goToAuth: () => void
  renderError: (key: string, message: string, withWarningIcon?: boolean) => JSX.Element
}

const PasswordResetForm = (props: Props) => {
  const dispatch = useDispatch<AppDispatch>()
  const { username, mobile, otpCode, password, repeatPassword, resetinput, goToAuth, renderError } = props
  const [error, setError] = useState<string>()

  const [passwordChanged, setPasswordChanged] = useState(false)
  const [otpSending, setOtpSending] = useState(false)
  const [passwordResetLoading, setPasswordResetLoading] = useState(false)
  const [otpEnabled, setOtpEnabled] = useState(false)
  const [rules, setRules] = useState<ValidatePasswordRules | null>()
  const [passwordValidationResult, setPasswordValidationResult] = useState<{
    [key: string]: boolean | undefined
  } | null>(null)

  const passwordValid = useMemo(() => {
    if (passwordValidationResult) {
      for (let key in passwordValidationResult) {
        if (passwordValidationResult[key] == false) {
          return false
        }
      }
      return true
    }
  }, [passwordValidationResult])

  const passwordResetEnabled = useMemo(() => {
    if (
      username?.length > 0 &&
      mobile?.length === 9 &&
      otpCode?.length === 7 &&
      password === repeatPassword &&
      passwordValid
    ) {
      return true
    }
    return false
  }, [username, mobile, otpCode, password, repeatPassword, passwordValid])

  useEffect(() => {
    if ((password || password === '') && rules) {
      setPasswordValidationResult(validatePassword(password, rules))
    }
  }, [password, rules])

  useEffect(() => {
    setError(undefined)
  }, [username, mobile, otpCode])

  const handleSendOTP = async () => {
    setPasswordValidationResult(null)
    setRules(null)
    resetinput('otpCode')
    if (otpSending) return
    try {
      setOtpSending(true)
      const result = await dispatch(verifyPasswordChangeOTP({ username: username, mobile: mobile }))
      if (result?.payload?.success) {
        setOtpEnabled(true)
        setPasswordValidationResult(validatePassword(password, result.payload.data.PassRules))
        setRules(result.payload.data.PassRules)
      } else {
        setError(result.payload.message)
      }
      setOtpSending(false)
    } catch (error) {
      setOtpSending(false)
    }
  }

  const handleResetPassword = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.currentTarget.blur()
    if (passwordResetLoading) return
    try {
      setPasswordResetLoading(true)
      const result = await dispatch(
        resetPassword({
          username: username,
          cellphone: mobile,
          newpass: password,
          code: otpCode.replace(/\s/g, ''),
        }),
      )
      if (result?.payload?.success) {
        dispatch(
          LKModalActions.openInfo(translateWeb('lbl_success_pass_recovery'), undefined, (closeInfo: () => void) => {
            goToAuth()
            closeInfo()
          }),
        )
      } else {
        setError(result.payload.message)
      }
      setPasswordResetLoading(false)
    } catch (error) {
      setPasswordResetLoading(false)
    }
  }

  useEffect(() => {
    if (otpCode?.length !== 7) {
      resetinput('password')
      resetinput('repeatPassword')
    }
  }, [otpCode])

  return (
    <div>
      {error && <div className="u-flex u-justifyCenter u-marginBottom-10">{renderError(error, error)}</div>}
      <div className="u-flex u-marginBottom-10">
        <div className="u-fluid u-marginRight-10">
          <p className="u-marginBottom-5">{translateLogin('lbl_username')}</p>
          {/* @ts-ignore */}
          <FormField
            name="username"
            icon={<StyledLKIcon alias="client" />}
            iconPosition="left"
            autoComplete="off"
            placeholder={translateLogin('lbl_user')}
            normalize={normalizeUsername}
            noErrorPopup
          />
        </div>

        <div className="u-fluid u-relative ">
          <p className="u-marginBottom-5">{translateLogin('lbl_password')}</p>
          {/* @ts-ignore */}
          <FormField
            disabled={otpCode?.length !== 7}
            name="password"
            onChange={() => {
              setPasswordChanged(true)
            }}
            icon={<StyledLKIcon alias="key" />}
            iconPosition="left"
            placeholder={translateLogin('lbl_password')}
            normalize={normalizePassword}
            required
            autoComplete="off"
            noErrorPopup
          />
          {passwordChanged && (
            <InputRightIcon>
              <LKIcon alias={passwordValid ? 'ok' : 'cancel'} />
            </InputRightIcon>
          )}
        </div>
      </div>
      <div className="u-flex u-marginBottom-20">
        <div className="u-fluid u-marginRight-10">
          <p className="u-marginBottom-5">{translateMain('lbl_number')}</p>
          {/* @ts-ignore */}
          <FormField
            name="mobile"
            icon={<StyledLKIcon alias="sms" />}
            iconPosition="left"
            placeholder={'T.:'}
            required
            autoComplete="off"
            normalize={normalizeMobileNumber}
            maxLength={9}
            noErrorPopup
          />
        </div>

        <div className="u-fluid u-relative">
          <p className="u-marginBottom-5">{translateMain('lbl_repeat_pass')}</p>
          {/* @ts-ignore */}
          <FormField
            disabled={otpCode?.length !== 7}
            name="repeatPassword"
            icon={<StyledLKIcon alias="key" />}
            iconPosition="left"
            placeholder={translateMain('lbl_repeat_pass')}
            normalize={normalizePassword}
            required
            autoComplete="off"
            noErrorPopup
          />
          {passwordChanged && (
            <InputRightIcon>
              <LKIcon alias={passwordValid && password === repeatPassword ? 'ok' : 'cancel'} />
            </InputRightIcon>
          )}
        </div>
      </div>
      <div className="u-flex u-marginBottom-20">
        <div className="u-fluid u-marginRight-10">
          <div className="u-marginBottom-10">
            <StyledLoginButton
              // name="submit"
              type="primary"
              disabled={!username || username?.length === 0 || mobile.length !== 9}
              onClick={handleSendOTP}
              fluid
            >
              {translateWeb('MSG_SMS_OTP_GET')}
            </StyledLoginButton>
          </div>
          <StyledResetInput>
            {/* @ts-ignore */}
            <FormField
              name="otpCode"
              disabled={!otpEnabled}
              icon={<StyledLKIcon alias="sms" />}
              iconPosition="left"
              required
              autoComplete="off"
              noErrorPopup
              minLength={4}
              maxLength={7}
              normalize={normalizeOTP}
            />
          </StyledResetInput>
        </div>

        <div className="u-fluid">
          {!!rules && !!passwordValidationResult && (
            <PasswordValidationResult
              result={passwordValidationResult}
              rules={rules}
              label={'lbl_use'}
              translate={translateMain}
            />
          )}
        </div>
      </div>
      <div>
        <StyledLoginButton onClick={handleResetPassword} disabled={!passwordResetEnabled} type="primary" fluid>
          {translateMain('cmd_change_pass')}
        </StyledLoginButton>
      </div>
    </div>
  )
}

export default container(PasswordResetForm)
